import { redactUrl } from './url-redaction';

/**
 * Reduces a request URL to path + query.
 *
 * `ApiError.url` and `NetworkError.url` are absolute, and `redactUrl` keeps the
 * origin of an absolute URL. Without this, the same endpoint would group under
 * a different `http.url` on every host. The query survives to be filtered by
 * `redactUrl`'s allowlist rather than dropped here, which keeps the booking
 * shape that makes an error reproducible.
 *
 * Returns the input untouched when it is not absolute, and `undefined` when it
 * cannot be parsed at all.
 */
const toRequestPath = (url: string): string | undefined => {
  try {
    const { pathname, search } = new URL(url);
    return `${pathname}${search}`;
  } catch {
    return url.startsWith('/') ? url : undefined;
  }
};

/**
 * Derives HTTP attributes from an error thrown by the typed client: an
 * `ApiError` (the server answered non-2xx) or a `NetworkError` (the fetch
 * itself rejected, e.g. Safari's `Load failed`).
 *
 * Without `http.url` a 500 from `configuration` and one from `last-booking` are
 * indistinguishable, and a network failure cannot be tied to a request at all
 * (samera/samera#3593). A `NetworkError` has no status; it reports `network` so
 * a query can tell it apart from a server response.
 *
 * Matched on `name` rather than `instanceof` on purpose: importing
 * `shared/api/api-client` here would point the observability module, which is
 * imported first thing at boot, at the API layer.
 *
 * `http.method` is absent because neither error carries it — `Response` does
 * not expose the request method. The URL makes it redundant in practice.
 *
 * Returns `{}` for anything else.
 */
export const enrichApiError = (error: unknown): Record<string, unknown> => {
  if (!(error instanceof Error)) return {};
  if (error.name !== 'ApiError' && error.name !== 'NetworkError') return {};

  const { status, url } = error as Error & { status?: number; url?: string };
  const path = url && toRequestPath(url);

  return {
    'http.url': (path && redactUrl(path)) || undefined,
    'http.status_code':
      error.name === 'NetworkError' ? 'network' : String(status ?? 'unknown'),
  };
};
